import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSendLg } from "../../../graphql/hooks/invoices/useSendLg";
import type { SendLgInput } from "../../../graphql/hooks/invoices/useSendLg";
import Modal from "../Modal";
import SendLgModalContent from "../assessment/SendLgModalContent";
import type { InvoiceModel } from "../../../models/InvoiceModels";

interface Options {
  invoiceIds: string[];
  selectedInvoice?: InvoiceModel;
}

interface EmailParams {
  recipientEmail: string;
  message: string;
}

export const useSendLgFlow = ({ invoiceIds, selectedInvoice }: Options) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { sendLg, loading } = useSendLg({
    onCompleted: (data) => {
      if (data?.invoice?.sendLg?.success === true) {
        setSuccessMessage(t("invoiceAssessment.lgSentSuccess"));
        setTimeout(() => handleClose(), 3000);
      } else {
        setErrorMessage(t("errors.somethingWentWrong"));
      }
    },
    onError: () => {
      setErrorMessage(t("errors.somethingWentWrong"));
    },
  });

  const handleClose = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setModalOpen(false);
  };

  const onSend = (params: EmailParams) => {
    setErrorMessage(null);
    const input: SendLgInput = {
      content: params.message,
      recipientEmail: params.recipientEmail,
      invoiceIds,
    };
    sendLg({ variables: { input } });
  };

  const modal = modalOpen ? (
    <Modal onClose={handleClose} closeIcon>
      <SendLgModalContent
        selectedInvoices={selectedInvoice ? [selectedInvoice] : []}
        onClose={handleClose}
        onSend={onSend}
        loading={loading}
        errorMessage={errorMessage}
        successMessage={successMessage}
        recipientClientIdentifier={
          selectedInvoice?.invoiceRecipient?.client?.id ??
          selectedInvoice?.invoiceRecipient?.clientIdentifier ??
          "SAKNAS"
        }
        issuerClientIdentifier={
          selectedInvoice?.invoiceIssuer?.client?.id ?? "SAKNAS"
        }
      />
    </Modal>
  ) : null;

  return { openModal: () => setModalOpen(true), modal };
};
